import React, { useEffect, useRef, useState } from 'react';
import { Field } from 'react-final-form';
import classNames from 'classnames';
import { ValidationError } from '../../components';

import css from './FieldSelect.module.css';
import IconDropdownArrow from '../IconDropdownArrow/IconDropdownArrow';
import IconClock from '../IconClock/IconClock';

const FieldSelectComponent = props => {
  const {
    rootClassName,
    className,
    selectClassName,
    labelClassName,
    id,
    label,
    input,
    meta,
    children,
    onChange,
    showLabelAsDisabled,
    tab,
    startTimeInput,
    ...rest
  } = props;

  const [isClicked, setIsClicked] = useState(false);
  const selectRef = useRef(null);

  if (label && !id) {
    throw new Error('id required when a label is given');
  }

  const { valid, invalid, touched, error } = meta;

  // Error message and input error styles are only shown if the
  // field has been touched and the validation has failed.
  const hasError = touched && invalid && error;

  const selectClasses = classNames({
    [selectClassName]: selectClassName,
    [css.selectError]: hasError,
  });
  const handleChange = e => {

    input.onChange(e);
    if (onChange) {
      onChange(e.currentTarget.value);
    }
  };

  const handleClick = e => {
    e.stopPropagation();
    setIsClicked(!isClicked);
  };

  useEffect(() => {
    const handleClickOutside = e => {
      setIsClicked(false);
    }
    window.addEventListener('click', handleClickOutside);
    return () => window.removeEventListener('click', handleClickOutside);
  }, []);

  const selectProps = {
    className: selectClasses, id, ...input, onClick: handleClick,
    onChange: handleChange, ...rest
  };

  const labelClasses = classNames({
    [css.labelDisabled]: showLabelAsDisabled,
    [labelClassName]: !!labelClassName,
  });


  const classes = classNames(rootClassName || css.root, className);
  return (
    <div className={classes}>
      {label ? (
        <label htmlFor={id} className={labelClasses}>
          {label}
        </label>
      ) : null}
      <select style={{ appearance: "none", backgroundImage: "none" }} {...selectProps}>{children}</select>
      {tab !== 'timeRangeSelect' &&
        (<>
          {startTimeInput ?
            <span className={css.iconClock}>
              <IconClock />
            </span>
            :
            <span className={tab === 'availability' ? css.arrowContainerForAvailability : css.arrowContainer}>
              <IconDropdownArrow isClicked={isClicked} />
            </span>
          }
        </>
        )
      }
      <ValidationError fieldMeta={meta} />
    </div>
  );
};

/**
 * Final Form Field wrapping <select> input
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.selectClassName add more style rules to <select> component
 * @param {string} props.name Name of the input in Final Form
 * @param {string} props.id Label is optional, but if it is given, an id is also required so the label can reference the input in the `for` attribute
 * @param {ReactNode} props.label
 * @param {ReactNode} props.children
 * @param {boolean} props.disabled Whether the select element is disabled
 * @param {boolean} props.showLabelAsDisabled Whether the label is disabled
 * @returns {JSX.Element} Final Form Field containing <select> input
 */
const FieldSelect = props => {
  return <Field component={FieldSelectComponent} {...props} />;
};

export default FieldSelect;
